if (!customElements.get('sticky-section')) {
  class StickySection extends HTMLElement {
    static instances = [];

    constructor() {
      super();
    }

    connectedCallback() {
      if (!this.closest('.shopify-section-group-header-group')) return;

      this.header = this.closest('.shopify-section') || this;
      this.stickyType = this.getAttribute('data-sticky-type');
      this.headerIsAlwaysSticky =
        this.stickyType === 'always' ||
        this.stickyType === 'reduce-logo-size';
      this.headerBounds = {};

      if (this.headerIsAlwaysSticky) {
        this.header.style.zIndex = '20';
      } else if (this.stickyType === 'on-scroll-up') {
        this.header.style.zIndex = '10';
      }

      this.setHeaderHeight();

      window.matchMedia('(max-width: 989px)').addEventListener('change', this.setHeaderHeight.bind(this));

      if (this.headerIsAlwaysSticky) {
        this.header.classList.add('shopify-section-header-sticky');
      }

      this.currentScrollTop = 0;
      this.preventReveal = false;
      this.predictiveSearch = this.querySelector('predictive-search');

      this.onScrollHandler = this.onScroll.bind(this);
      this.hideHeaderOnScrollUp = () => (this.preventReveal = true);

      this.addEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
      window.addEventListener('scroll', this.onScrollHandler, false);

      this.createObserver();

      // Register instance and sort
      StickySection.instances.push(this);
      StickySection.sortInstances();

      // ResizeObserver to detect height changes and trigger recalculation
      this.resizeObserver = new ResizeObserver(() => {
        this.setHeaderHeight();
        StickySection.recalculateOffsets();
      });
      this.resizeObserver.observe(this.header);

      // Run an immediate offset calculation before the first paint
      requestAnimationFrame(() => StickySection.recalculateOffsets());

      // Wait for web fonts to finish loading before measuring the final header height
      // and enabling smooth transitions. This prevents the font-swap reflow (system fallback
      // font is often taller than the web font) from causing a jarring layout jump.
      document.fonts.ready.then(() => {
        this.setHeaderHeight();
        StickySection.recalculateOffsets();
        requestAnimationFrame(() => {
          this.isInitialLoadDone = true;
        });
      });
    }

    static sortInstances() {
      StickySection.instances.sort((a, b) => {
        if (a === b) return 0;
        // if A follows B, B is before A
        if (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING) {
          return -1;
        }
        return 1;
      });
    }

    // Returns the combined natural bounds (before any sticky pinning) of all
    // scroll-up instances, so every instance flips hide/reveal at the same
    // scroll position rather than each using its own threshold.
    static getGroupBounds() {
      const scrollUpInstances = StickySection.instances.filter(
        i => !i.headerIsAlwaysSticky && i.stickyType !== 'none'
      );
      if (!scrollUpInstances.length) return { top: 0, bottom: 0 };

      const tops = scrollUpInstances.map(i => i.headerBounds.top ?? 0);
      const bottoms = scrollUpInstances.map(i => i.headerBounds.bottom ?? 0);
      return {
        top: Math.min(...tops),
        bottom: Math.max(...bottoms),
      };
    }

    static recalculateOffsets() {
      let currentOffset = 0;

      StickySection.instances.forEach((instance) => {
        if (instance.stickyType === 'none') {
          instance.header.style.top = '';
          instance.currentStickyOffset = 0;
          return;
        }

        if (instance.isInitialLoadDone) {
          instance.header.style.transition = 'top 0.25s ease-in-out';
        } else {
          instance.header.style.transition = 'none';
        }

        const isStickyActive = instance.headerIsAlwaysSticky || instance.header.classList.contains('shopify-section-header-sticky');

        if (!isStickyActive) {
          instance.header.style.top = '';
          instance.currentStickyOffset = 0;
          return;
        }

        // Hidden items slide out of view and don't take up space for items underneath them
        const isHidden = instance.header.classList.contains('shopify-section-header-hidden');

        instance.currentStickyOffset = currentOffset;

        if (!isHidden) {
          instance.header.style.top = `${currentOffset}px`;
          currentOffset += instance.header.offsetHeight;
        } else {
          instance.header.style.top = `-${instance.header.offsetHeight}px`;
        }
      });
    }

    setHeaderHeight() {
      document.documentElement.style.setProperty('--header-height', `${this.header.offsetHeight}px`);
    }

    disconnectedCallback() {
      if (!this.closest('.shopify-section-group-header-group')) return;

      this.removeEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
      window.removeEventListener('scroll', this.onScrollHandler);
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }

      const index = StickySection.instances.indexOf(this);
      if (index > -1) {
        StickySection.instances.splice(index, 1);
      }
      StickySection.recalculateOffsets();
    }

    createObserver() {
      let observer = new IntersectionObserver((entries, observer) => {
        const rect = entries[0].boundingClientRect;
        this.headerBounds = {
          top: rect.top + window.pageYOffset,
          bottom: rect.bottom + window.pageYOffset
        };
        observer.disconnect();
      });

      observer.observe(this.header);
    }

    onScroll() {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

      if (this.predictiveSearch && this.predictiveSearch.isOpen) return;

      // Use the combined bounds of all scroll-up instances so every section
      // triggers hide/reveal at exactly the same scroll position.
      const groupBounds = StickySection.getGroupBounds();
      const myOffset = this.currentStickyOffset || 0;

      if (scrollTop > this.currentScrollTop && scrollTop > groupBounds.bottom) {
        this.header.classList.add('scrolled-past-header');
        if (this.preventHide) return;
        requestAnimationFrame(this.hide.bind(this));
      } else if (scrollTop < this.currentScrollTop && scrollTop > groupBounds.bottom) {
        this.header.classList.add('scrolled-past-header');
        if (!this.preventReveal) {
          requestAnimationFrame(this.reveal.bind(this));
        } else {
          window.clearTimeout(this.isScrolling);

          this.isScrolling = setTimeout(() => {
            this.preventReveal = false;
          }, 66);

          requestAnimationFrame(this.hide.bind(this));
        }
      } else if (scrollTop <= groupBounds.top - myOffset) {
        this.header.classList.remove('scrolled-past-header');
        requestAnimationFrame(this.reset.bind(this));
      }

      this.currentScrollTop = scrollTop;
    }

    hide() {
      if (this.headerIsAlwaysSticky) return;
      this.header.style.transitionDelay = '0s';
      this.header.classList.add('shopify-section-header-hidden', 'shopify-section-header-sticky');
      this.closeMenuDisclosure();
      this.closeSearchModal();
      StickySection.recalculateOffsets();
    }

    reveal() {
      if (this.headerIsAlwaysSticky) return;
      this.header.style.transitionDelay = '0s';
      this.header.classList.add('shopify-section-header-sticky', 'animate');
      this.header.classList.remove('shopify-section-header-hidden');
      StickySection.recalculateOffsets();
    }

    reset() {
      if (this.headerIsAlwaysSticky) return;
      this.header.style.transitionDelay = '0s';
      this.header.classList.remove('shopify-section-header-hidden', 'shopify-section-header-sticky', 'animate');
      StickySection.recalculateOffsets();
    }

    closeMenuDisclosure() {
      this.disclosures = this.disclosures || this.header.querySelectorAll('header-menu');
      this.disclosures.forEach((disclosure) => disclosure.close());
    }

    closeSearchModal() {
      this.searchModal = this.searchModal || this.header.querySelector('details-modal');
      if (this.searchModal) {
        this.searchModal.close(false);
      }
    }
  }
  customElements.define('sticky-section', StickySection);
}
